import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';

import { loginSuccess, loginFailure, logout, refreshSuccess, userInfoSuccess, roleConfigSuccess } from '../reducers/authReducer';
import { userAccountAjax } from '../../ajax/UserAccountAjax';
import { settingsAjax } from '../../ajax/SettingsAjax';
import { isSuccessfulLoginResponse, SuccessfulLoginResponse } from '../../types/Auth/SuccessfulLoginResponse';
import { isRefreshResponse } from "../../types/Auth/RefreshResponse";
import { ActionResponse, ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { isOnlineNow } from "../../offline/connectivity";
import { resetCachedQueriesIfUserChanged } from "../../offline/resetCachedQueriesIfUserChanged";
import { SsoProviderKey } from "../../utilities/helpers/pageVocabulary.generated";

type AuthDispatch = ThunkDispatch<unknown, unknown, UnknownAction>;

export const loadSessionInfo = () => async (dispatch: AuthDispatch) => {
  const [userDetails, roleConfig] = await Promise.all([
    userAccountAjax.getMyInfo(),
    settingsAjax.getRoleConfig(),
  ]);

  if (!userDetails || ErrorResponse.isErrorResponse(userDetails)) return false;
  if (!roleConfig || ErrorResponse.isErrorResponse(roleConfig)) return false;

  await resetCachedQueriesIfUserChanged(userDetails.email);
  dispatch(userInfoSuccess(userDetails));
  dispatch(roleConfigSuccess(roleConfig));
  return true;
};

export type SignInFailure = { ok: false; error: string; noAccount?: true };

export type SignInResult = { ok: true } | SignInFailure;

const refuse = (dispatch: AuthDispatch, error: string): SignInFailure => {
  dispatch(loginFailure(error));
  return { ok: false, error };
};

export const loginUser = (email: string, password: string) => async (dispatch: AuthDispatch): Promise<SignInResult> => {
  try {
    const response = await userAccountAjax.login(email, password);

    if (!isSuccessfulLoginResponse(response))
      return refuse(dispatch, 'Login failed. Please check your credentials and try again.');

    dispatch(loginSuccess(response));

    return await dispatch(loadSessionInfo()) ? { ok: true } : refuse(dispatch, 'Failed to retrieve user details.');
  } catch {
    return refuse(dispatch, 'Login failed. Please try again.');
  }
};

const completeExternalLogin = async (
  dispatch: AuthDispatch,
  signIn: () => Promise<ActionResponse<SuccessfulLoginResponse | null>>,
): Promise<SignInResult> => {
  try {
    const response = await signIn();

    if (!isSuccessfulLoginResponse(response)) {
      const [refusal] = response.errors ?? [];
      const failed = refuse(dispatch, refusal?.description || 'No account was found for that sign-in. Ask an administrator to create one first.');
      return refusal?.code === 404 ? { ...failed, noAccount: true } : failed;
    }

    dispatch(loginSuccess(response));

    return await dispatch(loadSessionInfo()) ? { ok: true } : refuse(dispatch, 'Failed to retrieve user details.');
  } catch {
    return refuse(dispatch, 'Sign-in failed. Please try again.');
  }
};

export const loginWithSso = (provider: SsoProviderKey, idToken: string, zoneIds: number[] = []) => async (dispatch: AuthDispatch) =>
  completeExternalLogin(dispatch, () => userAccountAjax.loginWithProvider(provider, idToken, zoneIds));

export const loginWithLinkedIn = (code: string, state: string, nonce: string | null, zoneIds: number[] = []) => async (dispatch: AuthDispatch) =>
  completeExternalLogin(dispatch, () => userAccountAjax.loginWithLinkedIn(code, state, nonce, zoneIds));

export const logoutUser = () => async (dispatch: AuthDispatch) => {
  dispatch(logout())
};

export const refreshToken = (refreshToken: string) => async (dispatch: AuthDispatch) => {
  if (!isOnlineNow()) return null;

  try {
    const response = await userAccountAjax.refreshToken(refreshToken);
    if (isRefreshResponse(response)) {
      dispatch(refreshSuccess(response));
      return response;
    }

    const rejectedByServer = Array.isArray(response?.errors)
      && response.errors.some(e => e.code === 400 || e.code === 401 || e.code === 403);

    if (rejectedByServer) {
      console.error('Refresh token rejected by the server; signing out', response);
      dispatch(logout());
    } else {
      console.error('Token refresh failed without a rejection; keeping the session', response);
    }
    return null;
  } catch {
    return null;
  }
}